import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@atoms/Button';
import { CartItemRow } from '@molecules/CartItem';
import { useCartStore } from '@store/cartStore';
import { useOrderSuccessStore } from '@store/uiStore';
import { orderService } from '@services/orderService';
import { formatCurrency } from '@utils/formatters';
import { getErrorMessage } from '@services/api';

export function CartDrawer() {
  const {
    isOpen,
    closeDrawer,
    items,
    subtotal,
    deliveryFee,
    discountTotal,
    total,
    wholesalerId,
    wholesalerName,
    minOrderAmount,
    geoWarning,
    isSyncing,
    updateItem,
    removeItem,
    clearCart,
  } = useCartStore();

  const [orderType, setOrderType] = useState<'delivery' | 'pickup'>('delivery');
  const [error, setError] = useState<string | null>(null);
  const showOrderSuccess = useOrderSuccessStore((s) => s.showOrderSuccess);

  const queryClient = useQueryClient();

  const checkoutMutation = useMutation({
    mutationFn: () => {
      if (!wholesalerId || !items.length) {
        throw new Error('Sepet boş');
      }
      return orderService.createOrder({
        wholesalerId,
        items: items.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
        })),
        orderType,
      });
    },
    onSuccess: async (order) => {
      await clearCart();
      queryClient.invalidateQueries({ queryKey: ['restaurant', 'orders'] });
      closeDrawer();
      showOrderSuccess(order.id);
    },
    onError: (err) => setError(getErrorMessage(err, 'Sipariş oluşturulamadı')),
  });

  if (!isOpen) return null;

  const belowMin = minOrderAmount !== undefined && subtotal < minOrderAmount;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40" onClick={closeDrawer} aria-hidden />
      <aside className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-4">
          <div>
            <h2 className="text-lg font-bold">Sepetim</h2>
            {wholesalerName && <p className="text-sm text-gray-500">{wholesalerName}</p>}
          </div>
          <button type="button" onClick={closeDrawer} className="rounded-lg p-2 hover:bg-gray-100">
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4">
          {items.length === 0 ? (
            <p className="py-12 text-center text-gray-500">Sepetiniz boş</p>
          ) : (
            items.map((item) => (
              <CartItemRow
                key={item.id}
                item={item}
                disabled={isSyncing}
                onUpdate={(qty) => updateItem(item.id, qty)}
                onRemove={() => removeItem(item.id)}
              />
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t p-4">
            <div className="mb-3 flex gap-2">
              {(['delivery', 'pickup'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setOrderType(type)}
                  className={`flex-1 rounded-lg border py-2 text-sm font-medium ${
                    orderType === type
                      ? 'border-brand-500 bg-brand-50 text-brand-700'
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  {type === 'delivery' ? 'Teslimat' : 'Gel-Al'}
                </button>
              ))}
            </div>

            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Ara Toplam</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              {deliveryFee > 0 && (
                <div className="flex justify-between">
                  <span>Teslimat</span>
                  <span>{formatCurrency(deliveryFee)}</span>
                </div>
              )}
              {discountTotal > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>İndirim</span>
                  <span>-{formatCurrency(discountTotal)}</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-2 text-base font-bold">
                <span>Toplam</span>
                <span className="text-brand-600">{formatCurrency(total)}</span>
              </div>
            </div>

            {belowMin && (
              <p className="mt-2 text-xs text-amber-600">
                Minimum sipariş: {formatCurrency(minOrderAmount!)}
              </p>
            )}
            {geoWarning && (
              <p className="mt-2 text-xs text-amber-600">{geoWarning}</p>
            )}
            {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

            <Button
              fullWidth
              className="mt-4"
              loading={checkoutMutation.isPending || isSyncing}
              disabled={belowMin || !wholesalerId || !items.length}
              onClick={() => {
                setError(null);
                checkoutMutation.mutate();
              }}
            >
              Sipariş Ver
            </Button>
          </div>
        )}
      </aside>
    </>
  );
}
